import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useParlay } from '../../store';
import { useEvent, useUser } from '../../store';
import { saveEvent, formatCurrency } from '../../utils';
import type { Bet } from '../../types';

/**
 * Parlay Widget
 * Fixed overlay on bottom right for building parlay bets
 */
export function ParlayWidget() {
  const { isActive, legs, removeLeg, clearParlay, getTotalOdds, getDecimalOdds } = useParlay();
  const { event, updateEvent } = useEvent();
  const { currentParticipant } = useUser();
  const navigate = useNavigate();
  
  const [amount, setAmount] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);

  if (!isActive) return null;

  const totalOdds = getTotalOdds();
  const decimalOdds = getDecimalOdds();
  const betAmount = parseFloat(amount) || 0;
  const potentialWin = betAmount > 0 ? betAmount * (decimalOdds - 1) : 0;
  const potentialPayout = betAmount + potentialWin;

  const handleMakeBet = () => {
    setError('');

    if (!event || !currentParticipant) {
      setError('Event or participant not found');
      return;
    }

    if (legs.length < 2) {
      setError('Parlay must have at least 2 legs');
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid bet amount');
      return;
    }

    if (parsedAmount > currentParticipant.totalAvailable) {
      setError('Insufficient balance');
      return;
    }

    // Show confirmation screen
    setShowConfirmation(true);
  };

  const handleConfirmBet = async () => {
    if (!event || !currentParticipant) return;

    const parsedAmount = parseFloat(amount);

    try {
      // Final validation: Check for duplicate fights
      const fightIds = legs.map(leg => leg.fightId);
      const uniqueFightIds = new Set(fightIds);
      if (fightIds.length !== uniqueFightIds.size) {
        setError('Cannot have multiple bets from the same fight in a parlay');
        return;
      }
      
      // Create a single parlay bet with all legs
      const parlayBet: Bet = {
        id: `parlay-${Date.now()}`,
        participantId: currentParticipant.id,
        fightId: 'parlay', // Special fightId for parlay bets
        amount: parsedAmount,
        odds: totalOdds,
        placedAt: Date.now(),
        status: 'pending',
        isParlay: true,
        parlayLegs: legs.map(leg => {
          const fight = event.fights.find(f => f.id === leg.fightId);
          const fighter = leg.type === 'moneyline' && fight
            ? fight.fighters.find(f => f.name === leg.selection)
            : undefined;
          
          return {
            fightId: leg.fightId,
            fightName: leg.fightName,
            selection: leg.selection,
            odds: leg.odds,
            fighterId: fighter?.id,
            marketKey: leg.type === 'prop' ? leg.marketName : undefined,
            outcomeName: leg.type === 'prop' ? leg.selection : undefined,
            status: 'pending',
          };
        }),
      };
      
      const newBets = [parlayBet];

      // Update participant
      const updatedParticipant = {
        ...currentParticipant,
        bets: [...currentParticipant.bets, ...newBets],
        totalAvailable: currentParticipant.totalAvailable - parsedAmount,
      };

      // Update event
      const updatedEvent = {
        ...event,
        participants: event.participants.map((p) =>
          p.id === currentParticipant.id ? updatedParticipant : p
        ),
      };

      updateEvent(updatedEvent);
      await saveEvent(updatedEvent);

      // Clear parlay and navigate to dashboard
      clearParlay();
      navigate(`/event/${event.id}`);
    } catch (err) {
      setError('Failed to place parlay bet');
      console.error('Error placing parlay:', err);
    }
  };

  if (showConfirmation) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card padding="lg" className="w-full max-w-md max-h-[80vh] overflow-y-auto">
          <h2 className="text-xl font-bold text-primary-text mb-4">Confirm Parlay Bet</h2>
          
          <div className="space-y-3 mb-6">
            {legs.map((leg, index) => (
              <div key={index} className="p-3 bg-tertiary-bg rounded-lg">
                <div className="font-medium text-primary-text">{leg.fightName}</div>
                <div className="text-sm text-secondary-text mt-1">
                  {leg.marketName && <span className="mr-2">{leg.marketName}:</span>}
                  <span className="font-medium">{leg.selection}</span>
                  <span className="ml-2">({leg.odds > 0 ? '+' : ''}{leg.odds})</span>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2 mb-6 p-4 bg-secondary-bg rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-secondary-text">Total Legs:</span>
              <span className="text-primary-text font-medium">{legs.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-secondary-text">Parlay Odds:</span>
              <span className="text-primary-text font-medium">
                {totalOdds > 0 ? '+' : ''}{totalOdds}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-secondary-text">Bet Amount:</span>
              <span className="text-primary-text font-medium">{formatCurrency(betAmount)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t border-border pt-2 mt-2">
              <span className="text-secondary-text">Potential Payout:</span>
              <span className="text-success-green">{formatCurrency(potentialPayout)}</span>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error rounded-lg">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setShowConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={handleConfirmBet}
            >
              Confirm Parlay
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-primary-blue text-white px-4 py-2 rounded-lg shadow-lg hover:bg-blue-600 transition-colors"
        >
          Parlay ({legs.length}) 🎯
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-80 z-40">
      <Card padding="md" className="shadow-2xl">
        <div className="mb-3">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-bold text-primary-text">Parlay Builder</h3>
          </div>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => setIsMinimized(true)}
          >
            Minimize
          </Button>
        </div>

        {legs.length === 0 ? (
          <div className="text-center py-4 text-secondary-text text-sm">
            Add bets to your parlay
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
              {legs.map((leg, index) => (
                <div key={index} className="p-2 bg-tertiary-bg rounded flex justify-between items-start text-sm">
                  <div className="flex-1 mr-2">
                    <div className="font-medium text-primary-text text-xs">{leg.fightName}</div>
                    <div className="text-xs text-secondary-text mt-1">
                      {leg.marketName && <span className="mr-1">{leg.marketName}:</span>}
                      <span className="font-medium">{leg.selection}</span>
                      <span className="ml-1">({leg.odds > 0 ? '+' : ''}{leg.odds})</span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeLeg(index)}
                    className="text-error hover:text-red-400 text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="mb-3 p-2 bg-secondary-bg rounded text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-secondary-text">Legs:</span>
                <span className="text-primary-text font-medium">{legs.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-text">Odds:</span>
                <span className="text-primary-text font-medium">
                  {totalOdds > 0 ? '+' : ''}{totalOdds}
                </span>
              </div>
            </div>

            <Input
              label="Bet Amount"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={error}
            />

            {betAmount > 0 && (
              <div className="mt-2 p-2 bg-success-green/10 border border-success-green rounded text-sm">
                <div className="flex justify-between">
                  <span className="text-secondary-text">Potential Win:</span>
                  <span className="text-success-green font-bold">{formatCurrency(potentialWin)}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-secondary-text">Total Payout:</span>
                  <span className="text-success-green font-bold">{formatCurrency(potentialPayout)}</span>
                </div>
              </div>
            )}

            <Button
              variant="primary"
              fullWidth
              onClick={handleMakeBet}
              disabled={legs.length < 2 || betAmount <= 0}
              className="mt-3"
            >
              Make Parlay Bet
            </Button>

            <Button
              variant="danger"
              fullWidth
              onClick={clearParlay}
              className="mt-2"
            >
              Clear Bets and Close
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}

